/**
 * The portal's da/en microcopy (docs/10 section 5 is the source; D15: two languages in v1,
 * more locales are seed data, never code). Interpolating strings are functions so every
 * sentence stays single-sourced next to its key.
 */
export interface PortalStrings {
  readonly skipToContent: string;
  readonly chooseLanguage: string;
  readonly poweredBy: string;
  readonly whatAiSeesLink: string;

  readonly listTitle: string;
  readonly listIntro: string;
  readonly positionCount: (n: number) => string;
  readonly applyTimePill: string;
  readonly reviewWindowPill: string;
  readonly loadingPositions: string;
  readonly emptyTitle: string;
  readonly emptyBody: string;
  readonly deadlineChip: (date: string) => string;
  readonly daysLeftShort: (n: number) => string;
  readonly deadlinePassedShort: string;
  readonly deadlineToday: string;
  readonly applyButton: string;
  readonly loadError: string;
  readonly positionLoadError: string;
  readonly retry: string;

  readonly employmentTypes: Readonly<Record<string, string>>;

  readonly backToList: string;
  readonly aboutRole: string;
  readonly processTitle: string;
  readonly formTitle: string;
  readonly nameLabel: string;
  readonly emailLabel: string;
  readonly cvLabel: string;
  readonly chooseFile: string;
  readonly fileSelected: (name: string, size: string) => string;
  readonly motivationLabel: string;
  readonly githubLabel: string;
  readonly linkedinLabel: string;
  readonly portfolioLabel: string;
  readonly aiNoticeTitle: string;
  readonly requiredMark: string;
  readonly submitHint: string;
  readonly submitting: string;
  readonly deadlinePassedNotice: (date: string) => string;
  readonly closedNotice: string;
  readonly notFoundNotice: string;
  readonly submitFailed: string;
  readonly sendFailed: string;
  readonly threadLoadError: string;
  readonly wtsLoadError: string;
  readonly errNameRequired: string;
  readonly errNameTooLong: string;
  readonly errEmailRequired: string;
  readonly errEmailInvalid: string;
  readonly errCvRequired: string;
  readonly errCvType: string;
  readonly errCvSize: string;
  readonly errMotivationTooLong: string;

  readonly confirmTitle: string;
  readonly confirmSentAt: (datetime: string) => string;
  readonly confirmNext: string;
  readonly backToPositions: string;

  readonly appTitle: string;
  readonly invalidLink: string;
  readonly invalidLinkBody: string;
  readonly conversationLabel: string;
  readonly nowWhat: string;
  readonly nextSubmitted: string;
  readonly nextWaiting: (n: number, m: number, minutes: number) => string;
  readonly nextUnderReview: string;
  readonly nextDecision: string;
  readonly nextWithdrawn: string;
  readonly openConversation: string;
  readonly roundIndicator: (n: number, m: number) => string;
  readonly roundSentAt: (date: string) => string;
  readonly roundAnsweredAt: (date: string) => string;
  readonly roundWaiting: string;
  readonly answeredChip: string;
  readonly whyLabel: string;
  readonly answerLabel: string;
  readonly answerPlaceholder: string;
  readonly evidenceLabel: string;
  readonly evidenceHint: string;
  readonly evidencePrefix: string;
  readonly sendAnswers: string;
  readonly sending: string;
  readonly autosaveNote: string;
  readonly emailParity: string;
  readonly sentReceipt: (datetime: string) => string;
  readonly meterFull: string;
  readonly timelineTitle: string;
  readonly timelineRoundSent: (n: number) => string;
  readonly timelineAnswersReceived: (n: number) => string;
  readonly deadlinePassedBanner: string;
  readonly rightsTitle: string;
  readonly rightHumanReview: string;
  readonly rightEdit: string;
  readonly rightExport: string;
  readonly rightDelete: string;
  readonly rightsNote: string;

  readonly stepDone: string;
  readonly stepNow: string;
  readonly stepPending: string;
  readonly stepperLabel: string;
  readonly stepperSummary: (n: number, m: number, current: string, next: string | null) => string;

  readonly wtsTitle: string;
  readonly wtsProcesses: string;
  readonly wtsNeverSees: string;
  readonly wtsProduces: string;
  readonly wtsContact: string;
  readonly wtsRights: string;

  readonly loadingThread: string;
  readonly loadingPosition: string;
  readonly loadingWts: string;
}

export const DA_STRINGS: PortalStrings = {
  skipToContent: 'Spring til indhold',
  chooseLanguage: 'Vælg sprog',
  poweredBy: 'Drevet af rekruto.io',
  whatAiSeesLink: 'Hvad AI ser',

  listTitle: 'Ledige stillinger',
  listIntro:
    "Søg på ca. 5 minutter. AI'en stiller opfølgende spørgsmål, og en menneskelig rekrutterer " +
    'gennemgår altid din ansøgning, før der træffes en beslutning.',
  positionCount: (n) => (n === 1 ? '1 ledig stilling' : `${n} ledige stillinger`),
  applyTimePill: 'Ca. 5 min at søge',
  reviewWindowPill: 'Gennemgangen indledes senest 2 hverdage efter fristen',
  loadingPositions: 'Indlæser stillinger',
  emptyTitle: 'Der er ingen åbne stillinger lige nu.',
  emptyBody: 'Vi opdaterer her, når nye stillinger slås op.',
  deadlineChip: (date) => `Frist ${date}`,
  daysLeftShort: (n) => (n === 1 ? '1 dag tilbage' : `${n} dage tilbage`),
  deadlinePassedShort: 'Fristen er passeret',
  deadlineToday: 'Frist i dag',
  applyButton: 'Søg stillingen',
  loadError: 'Kunne ikke hente stillingerne. Prøv igen.',
  positionLoadError: 'Kunne ikke hente stillingen. Prøv igen.',
  retry: 'Prøv igen',

  employmentTypes: {
    full_time: 'Fastansættelse',
    part_time: 'Deltid',
    contract: 'Kontrakt',
    temporary: 'Midlertidig',
    internship: 'Praktik',
  },

  backToList: 'Tilbage til stillingerne',
  aboutRole: 'Om rollen',
  processTitle: 'Sådan forløber processen',
  formTitle: 'Ansøgningsformular',
  nameLabel: 'Fulde navn',
  emailLabel: 'E-mail',
  cvLabel: 'CV (PDF/DOCX, maks 10 MB)',
  chooseFile: 'Vælg fil',
  fileSelected: (name, size) => `${name}, ${size}, valgt`,
  motivationLabel: 'Kort om dig selv (valgfri)',
  githubLabel: 'GitHub',
  linkedinLabel: 'LinkedIn',
  portfolioLabel: 'Portefølje',
  aiNoticeTitle: 'Behandling med AI',
  requiredMark: 'påkrævet',
  submitHint: 'Tager ca. 5 min.',
  submitting: 'Sender …',
  deadlinePassedNotice: (date) =>
    `Fristen ${date} er passeret. Du kan ikke længere søge stillingen.`,
  closedNotice: 'Stillingen er lukket.',
  notFoundNotice: 'Vi kan ikke finde stillingen. Den er måske lukket eller fjernet.',
  submitFailed: 'Ansøgningen kunne ikke sendes. Prøv igen om et øjeblik.',
  sendFailed: 'Svaret kunne ikke sendes. Prøv igen om et øjeblik.',
  threadLoadError: 'Kunne ikke hente din ansøgning. Prøv igen.',
  wtsLoadError: 'Kunne ikke hente siden. Prøv igen.',
  errNameRequired: 'Skriv dit fulde navn.',
  errNameTooLong: 'Navnet er for langt (maks 200 tegn).',
  errEmailRequired: 'Skriv din e-mail.',
  errEmailInvalid: 'Indtast en gyldig e-mail.',
  errCvRequired: 'Vælg en CV-fil.',
  errCvType: "CV'et skal være en PDF- eller DOCX-fil.",
  errCvSize: 'Filen er for stor. Maks 10 MB.',
  errMotivationTooLong: 'Teksten er for lang (maks 8000 tegn).',

  confirmTitle: 'Din ansøgning er sendt',
  confirmSentAt: (datetime) => `Sendt ${datetime}`,
  confirmNext:
    'Gennemgangen indledes senest 2 hverdage efter fristen. Du får en e-mail med et direkte ' +
    'link til "Min ansøgning", hvor du kan følge status og svare på ' +
    "AI'ens opfølgende spørgsmål.",
  backToPositions: 'Til stillingerne',

  appTitle: 'Min ansøgning',
  invalidLink: 'Dette link er ikke længere gyldigt.',
  invalidLinkBody:
    'Linket til din ansøgning er udløbet eller forkert. Åbn det seneste link fra din e-mail, ' +
    'eller skriv til rekruttereren.',
  conversationLabel: 'Samtale',
  nowWhat: 'Hvad sker der nu?',
  nextSubmitted:
    "Din ansøgning er sendt. AI'en gennemgår den og vender tilbage med opfølgende spørgsmål, " +
    'hvis der mangler noget.',
  nextWaiting: (n, m, minutes) =>
    `Runde ${n} af ${m} venter på dine svar. Tager ca. ${minutes} min.`,
  nextUnderReview:
    "AI'en skriver sin anbefaling. En menneskelig rekrutterer gennemgår den altid, før der " +
    'træffes beslutning.',
  nextDecision: 'Beslutningen er klar. Du får detaljerne på e-mail.',
  nextWithdrawn: 'Din ansøgning er trukket tilbage.',
  openConversation: 'Åbn samtalen',
  roundIndicator: (n, m) => `Runde ${n} af ${m}`,
  roundSentAt: (date) => `sendt ${date}`,
  roundAnsweredAt: (date) => `besvaret ${date}`,
  roundWaiting: 'venter på dine svar',
  answeredChip: 'Besvaret',
  whyLabel: 'Hvorfor spørger AI om dette?',
  answerLabel: 'Dit svar',
  answerPlaceholder: 'Skriv dit svar …',
  evidenceLabel: 'Vedhæft bevis-link (valgfri)',
  evidenceHint: 'Linket gemmes sammen med dit svar som bevis.',
  evidencePrefix: 'Bevis:',
  sendAnswers: 'Send svar',
  sending: 'Sender …',
  autosaveNote: 'Svar gemmes automatisk, også mens du skriver.',
  emailParity: 'Svar gerne per e-mail i stedet - samme spørgsmål, samme frist.',
  sentReceipt: (datetime) => `Svar indsendt ${datetime}`,
  meterFull: 'Vi har det, vi behøver.',
  timelineTitle: 'Tidslinje',
  timelineRoundSent: (n) => `Runde ${n} sendt`,
  timelineAnswersReceived: (n) => `Svar på runde ${n} modtaget`,
  deadlinePassedBanner: 'Fristen er passeret. Ubesvarede runder er lukket.',
  rightsTitle: 'Dine rettigheder',
  rightHumanReview: 'Bed om menneskelig gennemgang',
  rightEdit: 'Rediger dine svar',
  rightExport: 'Eksportér dine data',
  rightDelete: 'Slet dine data',
  rightsNote:
    'Rettighederne varetager du via dit kandidat-login eller ved at skrive til rekruttereren - ' +
    'svaret kommer senest den næste hverdag.',

  stepDone: 'udfyldt',
  stepNow: 'aktivt trin',
  stepPending: 'afventer',
  stepperLabel: 'Procesforløb',
  stepperSummary: (n, m, current, next) =>
    `Trin ${n + 1} af ${m}: ${current}.` + (next ? ` Derefter ${next}.` : ''),

  wtsTitle: 'Hvad AI ser',
  wtsProcesses: "AI'en behandler",
  wtsNeverSees: "AI'en ser aldrig",
  wtsProduces: "AI'en producerer",
  wtsContact: 'Kontakt et menneske',
  wtsRights: 'Dine rettigheder',

  loadingThread: 'Indlæser din ansøgning',
  loadingPosition: 'Indlæser stillingen',
  loadingWts: 'Indlæser siden',
};

export const EN_STRINGS: PortalStrings = {
  skipToContent: 'Skip to content',
  chooseLanguage: 'Choose language',
  poweredBy: 'Powered by rekruto.io',
  whatAiSeesLink: 'What the AI sees',

  listTitle: 'Open positions',
  listIntro:
    'Applying takes about 5 minutes. The AI asks clarifying questions, and a human recruiter ' +
    'always reviews your application before a decision is made.',
  positionCount: (n) => (n === 1 ? '1 open position' : `${n} open positions`),
  applyTimePill: 'About 5 min to apply',
  reviewWindowPill: 'Review starts at most 2 business days after the deadline',
  loadingPositions: 'Loading positions',
  emptyTitle: 'No open positions right now.',
  emptyBody: 'We will post new openings here.',
  deadlineChip: (date) => `Deadline ${date}`,
  daysLeftShort: (n) => (n === 1 ? '1 day left' : `${n} days left`),
  deadlinePassedShort: 'The deadline has passed',
  deadlineToday: 'Deadline today',
  applyButton: 'Apply for this position',
  loadError: 'Could not load the positions. Try again.',
  positionLoadError: 'Could not load the position. Try again.',
  retry: 'Try again',

  employmentTypes: {
    full_time: 'Full time',
    part_time: 'Part time',
    contract: 'Contract',
    temporary: 'Temporary',
    internship: 'Internship',
  },

  backToList: 'Back to positions',
  aboutRole: 'About the role',
  processTitle: 'How the process runs',
  formTitle: 'Application form',
  nameLabel: 'Full name',
  emailLabel: 'Email',
  cvLabel: 'CV (PDF/DOCX, max 10 MB)',
  chooseFile: 'Choose file',
  fileSelected: (name, size) => `${name}, ${size}, selected`,
  motivationLabel: 'About you (optional)',
  githubLabel: 'GitHub',
  linkedinLabel: 'LinkedIn',
  portfolioLabel: 'Portfolio',
  aiNoticeTitle: 'Processing with AI',
  requiredMark: 'required',
  submitHint: 'Takes about 5 min.',
  submitting: 'Submitting …',
  deadlinePassedNotice: (date) => `The deadline of ${date} has passed. You can no longer apply.`,
  closedNotice: 'This position is closed.',
  notFoundNotice: 'We cannot find this position. It may have closed or been removed.',
  submitFailed: 'The application could not be submitted. Try again in a moment.',
  sendFailed: 'The answers could not be submitted. Try again in a moment.',
  threadLoadError: 'Could not load your application. Try again.',
  wtsLoadError: 'Could not load the page. Try again.',
  errNameRequired: 'Enter your full name.',
  errNameTooLong: 'The name is too long (max 200 characters).',
  errEmailRequired: 'Enter your email.',
  errEmailInvalid: 'Enter a valid email.',
  errCvRequired: 'Choose a CV file.',
  errCvType: 'The CV must be a PDF or DOCX file.',
  errCvSize: 'The file is too large. Max 10 MB.',
  errMotivationTooLong: 'The text is too long (max 8000 characters).',

  confirmTitle: 'Your application has been submitted',
  confirmSentAt: (datetime) => `Submitted ${datetime}`,
  confirmNext:
    'Review starts at most 2 business days after the deadline. You will receive an email with a ' +
    'direct link to "My application", where you can follow the status and answer the ' +
    "AI's clarifying questions.",
  backToPositions: 'Back to positions',

  appTitle: 'My application',
  invalidLink: 'This link is no longer valid.',
  invalidLinkBody:
    'The link to your application has expired or is wrong. Open the latest link from your email, ' +
    'or write to the recruiter.',
  conversationLabel: 'Conversation',
  nowWhat: 'What happens now?',
  nextSubmitted:
    'Your application has been submitted. The AI reviews it and comes back with clarifying ' +
    'questions if anything is missing.',
  nextWaiting: (n, m, minutes) =>
    `Round ${n} of ${m} is waiting for your answers. Takes about ${minutes} min.`,
  nextUnderReview:
    'The AI is writing its recommendation. A human recruiter always reviews it before a decision ' +
    'is made.',
  nextDecision: 'The decision is ready. You will get the details by email.',
  nextWithdrawn: 'Your application has been withdrawn.',
  openConversation: 'Open the conversation',
  roundIndicator: (n, m) => `Round ${n} of ${m}`,
  roundSentAt: (date) => `sent ${date}`,
  roundAnsweredAt: (date) => `answered ${date}`,
  roundWaiting: 'waiting for your answers',
  answeredChip: 'Answered',
  whyLabel: 'Why does the AI ask this?',
  answerLabel: 'Your answer',
  answerPlaceholder: 'Write your answer …',
  evidenceLabel: 'Attach evidence link (optional)',
  evidenceHint: 'The link is saved with your answer as evidence.',
  evidencePrefix: 'Evidence:',
  sendAnswers: 'Send answers',
  sending: 'Submitting …',
  autosaveNote: 'Answers save automatically as you write.',
  emailParity: 'You can reply by email instead - same questions, same deadline.',
  sentReceipt: (datetime) => `Answers submitted ${datetime}`,
  meterFull: 'We have what we need.',
  timelineTitle: 'Timeline',
  timelineRoundSent: (n) => `Round ${n} sent`,
  timelineAnswersReceived: (n) => `Answers to round ${n} received`,
  deadlinePassedBanner: 'The deadline has passed. Unanswered rounds are closed.',
  rightsTitle: 'Your rights',
  rightHumanReview: 'Request human review',
  rightEdit: 'Edit your answers',
  rightExport: 'Export your data',
  rightDelete: 'Delete your data',
  rightsNote:
    'You exercise the rights through your candidate login or by writing to the recruiter - the ' +
    'reply comes by the next business day.',

  stepDone: 'completed',
  stepNow: 'current step',
  stepPending: 'pending',
  stepperLabel: 'Process steps',
  stepperSummary: (n, m, current, next) =>
    `Step ${n + 1} of ${m}: ${current}.` + (next ? ` Next: ${next}.` : ''),

  wtsTitle: 'What the AI sees',
  wtsProcesses: 'The AI processes',
  wtsNeverSees: 'The AI never sees',
  wtsProduces: 'The AI produces',
  wtsContact: 'Reach a human',
  wtsRights: 'Your rights',

  loadingThread: 'Loading your application',
  loadingPosition: 'Loading the position',
  loadingWts: 'Loading the page',
};
